<template>
  <div class="section-card" :class="{ 'section-card--admin': adminOnly }">
    <div class="section-card__header" :class="{ 'section-card__header--centered': !desc }">
      <c-icon-badge :icon="icon" :color="color" />
      <div>
        <div class="section-card__title"><slot name="title">{{ title }}</slot></div>
        <div v-if="desc" class="section-card__desc" v-html="desc"></div>
      </div>
      <div v-if="$slots.actions" class="section-card__actions">
        <slot name="actions" />
      </div>
      <div v-if="adminOnly" class="section-card__admin-badge">
        <i class="fa-regular fa-shield-halved"></i>
        Admin only
      </div>
    </div>
    <div class="section-card__body">
      <!-- Chrome-first loading: the card frame, icon and title render
           immediately and only the body shimmers, so the page keeps its
           layout instead of popping in once the request lands. -->
      <app-skeleton
        v-if="loading"
        :lines="loadingLines"
        :pattern="loadingPattern"
      />
      <slot v-else />
    </div>
  </div>
</template>

<script>
export default {
  props: {
    icon:      { type: String, required: true },
    title:     { type: String, required: true },
    desc:      { type: String, default: '' },
    color:     { type: String, default: '' },  // green | purple | orange | teal | '' (default blue)
    adminOnly: { type: Boolean, default: false },

    // Render a skeleton in place of the slot while data is on the way.
    //
    // NOTE: this UNMOUNTS the slot. Do not use it on a card whose content is
    // built imperatively by a non-Vue library (jQuery plugins, hand-rolled
    // canvas, third-party editors) — the library loses its mount node and
    // never re-attaches. Those cards should keep their own
    // `<app-skeleton v-if>` plus a `v-show` wrapper instead; see
    // registration/form-builder for the pattern.
    loading:        { type: Boolean, default: false },
    loadingLines:   { type: Number, default: 4 },
    // 'default' (form fields) | 'rows' (table rows) | 'hero'
    loadingPattern: { type: String, default: 'default' },
  },
};
</script>

<style lang="scss" scoped>
.section-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;

  &--admin {
    border-left: 3px solid #fa5252;
  }

  &--admin &__header {
    background: #fff9f9;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 10px 20px;
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
    border-radius: 8px 8px 0 0;
    overflow: hidden;

    // No desc means the title is the only line next to the icon — center it
    // against the icon instead of hugging the top like a multi-line block does.
    &--centered {
      align-items: center;
    }
  }

&__title {
    // Lines up a count badge passed through the #title slot
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 0.95em;
    color: #333;
    margin-bottom: 0;
  }

  &__desc {
    font-size: 0.8em;
    color: #999;
  }

  &__body {
    padding: 20px;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-self: center;
    gap: 8px;
    margin-left: auto;
  }

  // Admin badge follows the actions instead of claiming the right edge itself
  &__actions + &__admin-badge {
    margin-left: 0;
  }

  // Only cards with actions wrap: on phones the buttons drop under the title
  &__header:has(.section-card__actions) {
    @media (max-width: 600px) {
      flex-wrap: wrap;
    }
  }

  &__actions {
    @media (max-width: 600px) {
      flex-basis: 100%;
      margin-left: 50px;
    }
  }

  &__admin-badge {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: #e03131;
    background: #fff5f5;
    border: 1px solid #ffc9c9;
    border-radius: 20px;
    padding: 3px 10px;
    white-space: nowrap;
    flex-shrink: 0;
  }
}
</style>
